/* In-app PDF viewer — right side panel (desktop) / full-screen sheet (mobile). */
/* Renders via PDF.js (react-pdf) rather than a native <iframe>, which is broken */
/* on iOS/mobile Safari (frozen first-page image or forced download). */

import { useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import pdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import styles from './pdf-panel.module.css';

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorker;

const MAX_PAGE_WIDTH = 1400;
const PANEL_WIDTH_KEY = 'pdf-panel-width';
const ZOOM_KEY = 'pdf-panel-zoom';
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 2.5;
const ZOOM_STEP = 0.2;
const clampWidth = (w: number) => Math.min(Math.max(w, 360), window.innerWidth * 0.95);
const clampZoom = (z: number) => Math.min(Math.max(z, MIN_ZOOM), MAX_ZOOM);

export default function PdfPanel({ url, title, onClose }: { url: string; title: string; onClose: () => void }) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const [bodyWidth, setBodyWidth] = useState(0);
  const [panelWidth, setPanelWidth] = useState(() => Number(localStorage.getItem(PANEL_WIDTH_KEY)) || 600);
  const [zoom, setZoom] = useState(() => clampZoom(Number(localStorage.getItem(ZOOM_KEY)) || 1));
  const [numPages, setNumPages] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const panRef = useRef<{ startX: number; startY: number; scrollLeft: number; scrollTop: number } | null>(null);
  const [isPanning, setIsPanning] = useState(false);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const measure = () => setBodyWidth(el.clientWidth - 24);
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    measure();
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    localStorage.setItem(PANEL_WIDTH_KEY, String(panelWidth));
  }, [panelWidth]);

  useEffect(() => {
    localStorage.setItem(ZOOM_KEY, String(zoom));
  }, [zoom]);

  const fitToWidth = () => {
    const base = Math.min(bodyWidth, MAX_PAGE_WIDTH);
    if (base <= 0) return;
    setZoom(clampZoom(bodyWidth / base));
  };

  const startPan = (e: React.PointerEvent) => {
    if (e.button !== undefined && e.button !== 0) return;
    const el = bodyRef.current;
    if (!el) return;
    el.setPointerCapture(e.pointerId);
    panRef.current = { startX: e.clientX, startY: e.clientY, scrollLeft: el.scrollLeft, scrollTop: el.scrollTop };
    setIsPanning(true);
  };

  const movePan = (e: React.PointerEvent) => {
    const pan = panRef.current;
    const el = bodyRef.current;
    if (!pan || !el) return;
    el.scrollLeft = pan.scrollLeft - (e.clientX - pan.startX);
    el.scrollTop = pan.scrollTop - (e.clientY - pan.startY);
  };

  const endPan = (e: React.PointerEvent) => {
    const el = bodyRef.current;
    if (el && el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
    panRef.current = null;
    setIsPanning(false);
  };

  const startResize = (e: React.PointerEvent) => {
    e.preventDefault();
    const onMove = (ev: PointerEvent) => setPanelWidth(clampWidth(window.innerWidth - ev.clientX));
    const onUp = () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  };

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-label={title}>
      <div className={styles.panel} style={{ width: `min(${panelWidth}px, 100%)` }}>
        <div
          className={styles.resizeHandle}
          onPointerDown={startResize}
          role="separator"
          aria-orientation="vertical"
          aria-label="Redimensionner le panneau"
        />
        <div className={styles.head}>
          <span className={styles.title}>{title}</span>
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.iconBtn}
              onClick={() => setZoom((z) => clampZoom(z - ZOOM_STEP))}
              disabled={zoom <= MIN_ZOOM}
              aria-label="Réduire"
              title="Réduire"
            >
              −
            </button>
            <span className={styles.zoomLevel} aria-live="polite">
              {Math.round(zoom * 100)}%
            </span>
            <button
              type="button"
              className={styles.iconBtn}
              onClick={() => setZoom((z) => clampZoom(z + ZOOM_STEP))}
              disabled={zoom >= MAX_ZOOM}
              aria-label="Agrandir"
              title="Agrandir"
            >
              +
            </button>
            <button
              type="button"
              className={styles.iconBtn}
              onClick={() => setZoom(1)}
              aria-label="Réinitialiser le zoom"
              title="Réinitialiser le zoom"
            >
              ⤢
            </button>
            <button
              type="button"
              className={styles.iconBtn}
              onClick={fitToWidth}
              aria-label="Ajuster à la largeur du panneau"
              title="Ajuster à la largeur du panneau"
            >
              ↔
            </button>
            <a className={styles.iconLink} href={url} target="_blank" rel="noreferrer" title="Télécharger" aria-label="Télécharger">
              ⤓
            </a>
            <button type="button" className={styles.iconBtn} onClick={onClose} aria-label="Fermer">
              ×
            </button>
          </div>
        </div>
        <div
          className={styles.body}
          ref={bodyRef}
          data-zoomed={zoom > 1 || undefined}
          data-panning={isPanning || undefined}
          onPointerDown={zoom > 1 ? startPan : undefined}
          onPointerMove={zoom > 1 ? movePan : undefined}
          onPointerUp={zoom > 1 ? endPan : undefined}
          onPointerCancel={zoom > 1 ? endPan : undefined}
        >
          {error ? (
            <p className={styles.msg}>Impossible d’afficher le PDF : {error}</p>
          ) : (
            <Document
              file={url}
              onLoadSuccess={({ numPages: n }) => setNumPages(n)}
              onLoadError={(e) => setError(e.message)}
              loading={<p className={styles.msg}>Chargement du devoir…</p>}
            >
              {Array.from({ length: numPages }, (_, i) => (
                <Page
                  key={i}
                  pageNumber={i + 1}
                  width={bodyWidth > 0 ? Math.min(bodyWidth, MAX_PAGE_WIDTH) * zoom : undefined}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  className={styles.page}
                />
              ))}
            </Document>
          )}
        </div>
      </div>
    </div>
  );
}
